import { useEffect, useState } from 'react';

/**
 * A garment's picture, or a placeholder saying there isn't one.
 *
 * <p>
 * <strong>There are two ways to have no picture and they must look the same.</strong> The server
 * sends `null` when nothing has been photographed and no design shot exists; separately, a presigned
 * URL expires after minutes, so a screen left open long enough will have links that 403. Both end as
 * the same tile — a garment glyph on the canvas — because to the person looking at it the difference
 * is nothing, and an empty box or a browser's broken-image icon reads as the screen having failed
 * rather than as the shop not having taken a photograph.
 * </p>
 *
 * <p>
 * <strong>The expiry case is why `onError` matters and why the key resets it.</strong> Without the
 * reset, a refetch that hands over a freshly signed URL would still render the placeholder, because
 * the failure was remembered against the component rather than against the link that failed.
 * </p>
 */
export function GarmentImage({
  url,
  alt,
  size = 'md',
  className = '',
  onClick,
}: {
  url: string | null | undefined;
  /** What the picture is of. Falls back to a generic label, never to an empty string. */
  alt: string;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  className?: string;
  /** Set when there is a viewer to open. The tile becomes a button and says so. */
  onClick?: (() => void) | undefined;
}) {
  const [failed, setFailed] = useState(false);

  // Keyed on the URL rather than cleared on mount: a refetch after expiry replaces the link, and a
  // failure remembered from the old one would keep the placeholder up over a picture that now works.
  useEffect(() => {
    setFailed(false);
  }, [url]);

  const box = SIZES[size];
  const showing = url && !failed;

  const inner = showing ? (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      onError={() => setFailed(true)}
      className="size-full rounded object-cover"
    />
  ) : (
    <span
      className="text-default flex size-full flex-col items-center justify-center gap-0.5 rounded bg-light"
      aria-label={`No photograph of ${alt}`}
      role="img"
    >
      <i className={`icon icon-image-off ${GLYPH[size]} opacity-45`} />
      {size === 'lg' && <span className="text-[10px] opacity-70">No photo</span>}
    </span>
  );

  const shell = `border-border-color shrink-0 overflow-hidden rounded border bg-white ${box} ${className}`;

  if (!onClick) {
    return <span className={shell}>{inner}</span>;
  }

  return (
    <button
      type="button"
      onClick={onClick}
      // Disabled rather than hidden when there is nothing to open: the tile still holds the layout,
      // and a row whose thumbnail sometimes is and sometimes isn't a button is a row that jumps.
      disabled={!showing}
      aria-label={showing ? `View ${alt}` : `No photograph of ${alt}`}
      className={`${shell} ${showing ? 'cursor-pointer hover:opacity-85' : 'cursor-default'}`}
    >
      {inner}
    </button>
  );
}

const SIZES: Record<'xs' | 'sm' | 'md' | 'lg', string> = {
  xs: 'size-8',
  sm: 'size-10',
  md: 'size-14',
  lg: 'h-32 w-full',
};

const GLYPH: Record<'xs' | 'sm' | 'md' | 'lg', string> = {
  xs: 'text-[12px]',
  sm: 'text-[14px]',
  md: 'text-[18px]',
  lg: 'text-[26px]',
};
